import React, { useState, useEffect, useContext } from "react";
import {
  Card,
  Badge,
  Spinner,
  Alert,
  Button,
  Row,
  Col,
  Modal,
  Form,
  ProgressBar,
} from "react-bootstrap";
import {
  IconBox,
  IconPlus,
  IconUpload,
  IconDownload,
  IconFileText,
  IconX,
  IconTrash,
  IconCheck,
  IconBrain,
} from "@tabler/icons-react";
import Swal from "sweetalert2";


import UserContext from "../../contexts/UserContext";
import ProductsServices from "../../services/ProductsServices";
import ProductTable from "./components/ProductTable";
import ProductSearch from "./components/ProductSearch";
import ProductFormWrapper from "./components/ProductFormWrapper";
import { useAgentHubContext } from "../AgentHub/contexts/AgentHubContext";

const ProductsList = () => {
  const { user } = useContext(UserContext);
  const { agent, agentId } = useAgentHubContext();

  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [agentFilter, setAgentFilter] = useState(agentId || "");
  const [statusFilter, setStatusFilter] = useState("");
  const [orderingFilter, setOrderingFilter] = useState("-created_at");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Estados do modal de importação
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);

  // Estados do modal de treinar agente
  const [showTrainModal, setShowTrainModal] = useState(false);
  const [training, setTraining] = useState(false);
  const [trainProgress, setTrainProgress] = useState(0);

  // Estados do modal de criação/edição de produto
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingProductId, setEditingProductId] = useState(null);

  // Estados para seleção de produtos
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showSelectionPanel, setShowSelectionPanel] = useState(false);
  const maxSelectionLimit = 100;

  const loadProductStats = async () => {
    try {
      setStatsLoading(true);
      const response = await ProductsServices.getProductStats();
      setStats(response.data || {});
    } catch (error) {
      console.error("Erro ao carregar estatísticas de produtos:", error);
    } finally {
      setStatsLoading(false);
    }
  };

  const loadProducts = async () => {
    // Evitar múltiplas chamadas simultâneas
    if (loading) return;

    try {
      setLoading(true);

      const filters = {
        page: currentPage,
        page_size: pageSize,
        search: searchTerm || undefined,
        agent_id: agentFilter || undefined,
        status: statusFilter || undefined,
        ordering: orderingFilter,
      };

      const response = await ProductsServices.listProducts(filters);

      if (response.success) {
        // A API retorna: { data: { count, next, previous, results }, products, count }
        const responseData = response.data || {};
        const productsData = responseData.results || response.products || [];
        const count = responseData.count || response.count || 0;

        // Formatar dados para exibição
        const formattedProducts = productsData.map((product) =>
          ProductsServices.formatProductData(product)
        );

        setProducts(formattedProducts);
        setTotalCount(count);
        setTotalPages(Math.ceil(count / pageSize));

        // Atualizar estado de "selecionar todos" baseado na página atual
        const currentPageIds = formattedProducts.map((p) => p.id);
        const selectedOnCurrentPage = selectedProducts.filter((id) =>
          currentPageIds.includes(id)
        );
        setSelectAll(
          selectedOnCurrentPage.length === currentPageIds.length &&
            currentPageIds.length > 0
        );
      } else {
        throw new Error(response.error);
      }
    } catch (error) {
      console.error("Erro ao carregar produtos:", error);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Não foi possível carregar os produtos.",
        confirmButtonColor: "#dc3545",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProductStats();
    loadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    searchTerm,
    agentFilter,
    statusFilter,
    orderingFilter,
    pageSize,
  ]);

  const handleSearch = (value) => {
    // Só executar se o valor realmente mudou
    if (value !== searchTerm) {
      setSearchTerm(value);
      setCurrentPage(1);
    }
  };

  const handleAgentFilter = (agentId) => {
    if (agentId !== agentFilter) {
      setAgentFilter(agentId);
      setCurrentPage(1);
    }
  };

  const handleStatusFilter = (status) => {
    if (status !== statusFilter) {
      setStatusFilter(status);
      setCurrentPage(1);
    }
  };

  const handleOrderingFilter = (ordering) => {
    if (ordering !== orderingFilter) {
      setOrderingFilter(ordering);
      setCurrentPage(1);
    }
  };

  const handlePageSizeChange = (newPageSize) => {
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
      setCurrentPage(1);
    }
  };

  const handleCreateProduct = () => {
    setShowCreateModal(true);
  };

  const handleProductCreated = (product) => {
    setShowCreateModal(false);
    loadProducts();
    loadProductStats();
  };

  const handleCancelCreate = () => {
    setShowCreateModal(false);
    setEditingProductId(null);
  };

  const handleImportProducts = () => {
    setShowImportModal(true);
  };

  const handleTrainAgent = () => {
    setShowTrainModal(true);
  };


  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validar tipo de arquivo
      const allowedTypes = [
        "text/csv",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ];

      if (!allowedTypes.includes(file.type)) {
        Swal.fire({
          icon: "error",
          title: "Arquivo inválido",
          text: "Por favor, selecione um arquivo CSV ou Excel (.xlsx).",
          confirmButtonColor: "#dc3545",
        });
        return;
      }

      // Validar tamanho (máx 5MB)
      if (file.size > 5 * 1024 * 1024) {
        Swal.fire({
          icon: "error",
          title: "Arquivo muito grande",
          text: "O arquivo deve ter no máximo 5MB.",
          confirmButtonColor: "#dc3545",
        });
        return;
      }

      setSelectedFile(file);
    }
  };

  const handleDownloadSample = async () => {
    try {
      const response = await ProductsServices.downloadImportSample();

      if (response.success) {
        // Criar link para download
        const blob = new Blob([response.data], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "exemplo_importacao_produtos.csv";
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error("Erro ao baixar arquivo exemplo:", error);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Não foi possível baixar o arquivo exemplo.",
        confirmButtonColor: "#dc3545",
      });
    }
  };

  const handleImportFile = async () => {
    if (!selectedFile) {
      Swal.fire({
        icon: "warning",
        title: "Arquivo obrigatório",
        text: "Selecione um arquivo para importar.",
        confirmButtonColor: "#ffc107",
      });
      return;
    }

    try {
      setImporting(true);
      setImportProgress(0);

      // Simular progresso
      const progressInterval = setInterval(() => {
        setImportProgress((prev) => Math.min(prev + 10, 90));
      }, 200);

      const response = await ProductsServices.importProducts(
        selectedFile,
        agentId
      );

      clearInterval(progressInterval);
      setImportProgress(100);

      if (response.success) {
        setTimeout(() => {
          setImportProgress(0);
          setImporting(false);
          setShowImportModal(false);
          setSelectedFile(null);

          // Mostrar relatório de importação
          const report = response.data;
          Swal.fire({
            icon: "success",
            title: "Importação concluída!",
            html: `
              <div style="text-align: left; padding: 10px;">
                <p><strong>✅ Produtos importados:</strong> ${
                  report.created_count || 0
                }</p>
                ${
                  report.errors && report.errors.length > 0
                    ? `<p><strong>❌ Erros:</strong> ${report.errors.length}</p>
                   <div style="max-height: 150px; overflow-y: auto; background: #f8f9fa; padding: 10px; border-radius: 5px;">
                     ${report.errors
                       .map((error) => `<small>• ${error}</small>`)
                       .join("<br>")}
                   </div>`
                    : ""
                }
                ${
                  report.warnings && report.warnings.length > 0
                    ? `<p><strong>⚠️ Avisos:</strong> ${
                        report.warnings.length
                      }</p>
                   <div style="max-height: 100px; overflow-y: auto; background: #fff3cd; padding: 10px; border-radius: 5px;">
                     ${report.warnings
                       .map((warning) => `<small>• ${warning}</small>`)
                       .join("<br>")}
                   </div>`
                    : ""
                }
              </div>
            `,
            confirmButtonColor: "#28a745",
          });

          // Recarregar dados
          loadProducts();
          loadProductStats();
        }, 1000);
      } else {
        throw new Error(response.error);
      }
    } catch (error) {
      console.error("Erro na importação:", error);
      setImporting(false);
      setImportProgress(0);

      const alertConfig = ProductsServices.showErrorAlert(
        error,
        "Erro na importação"
      );
      Swal.fire(alertConfig);
    }
  };

  const handleCloseImportModal = () => {
    if (!importing) {
      setShowImportModal(false);
      setSelectedFile(null);
      setImportProgress(0);
    }
  };

  const handleTrainAgentRAG = async () => {

    try {
      setTraining(true);
      setTrainProgress(0);

      // Simular progresso
      const progressInterval = setInterval(() => {
        setTrainProgress((prev) => Math.min(prev + 10, 90));
      }, 200);

      const response = await ProductsServices.trainAgentRAG(
        agentId
      );

      clearInterval(progressInterval);
      setTrainProgress(100);

      if (response.success) {
        setTimeout(() => {
          setTrainProgress(0);
          setTraining(false);
          setShowTrainModal(false);

          // Mostrar relatório de treinamento
          const report = response.data;
          Swal.fire({
            icon: "success",
            title: "Treinamento concluído!",
            html: `
              <div style="text-align: left; padding: 10px;">
                <p><strong>🤖 Agente:</strong> ${report.agent_name}</p>
                <p><strong>📦 Total de produtos:</strong> ${
                  report.total_products || 0
                }</p>
                <p><strong>✅ Produtos processados:</strong> ${
                  report.processed_products || 0
                }</p>
                <p><strong>🧠 Embeddings criados:</strong> ${
                  report.successful_embeddings || 0
                }</p>
                <p><strong>📊 Chunks de dados:</strong> ${
                  report.total_chunks || 0
                }</p>
                ${
                  report.previous_embeddings_cleared > 0
                    ? `<p><strong>🗑️ Embeddings anteriores removidos:</strong> ${report.previous_embeddings_cleared}</p>`
                    : ""
                }
                ${
                  report.errors && report.errors.length > 0
                    ? `<p><strong>❌ Erros:</strong> ${report.errors.length}</p>
                   <div style="max-height: 150px; overflow-y: auto; background: #f8f9fa; padding: 10px; border-radius: 5px;">
                     ${report.errors
                       .map((error) => `<small>• ${error}</small>`)
                       .join("<br>")}
                   </div>`
                    : ""
                }
                ${
                  report.warnings && report.warnings.length > 0
                    ? `<p><strong>⚠️ Avisos:</strong> ${
                        report.warnings.length
                      }</p>
                   <div style="max-height: 100px; overflow-y: auto; background: #fff3cd; padding: 10px; border-radius: 5px;">
                     ${report.warnings
                       .map((warning) => `<small>• ${warning}</small>`)
                       .join("<br>")}
                   </div>`
                    : ""
                }
              </div>
            `,
            confirmButtonColor: "#28a745",
            width: 600,
          });
        }, 1000);
      } else {
        throw new Error(response.error);
      }
    } catch (error) {
      console.error("Erro no treinamento:", error);
      setTraining(false);
      setTrainProgress(0);

      const alertConfig = ProductsServices.showErrorAlert(
        error,
        "Erro no treinamento do agente"
      );
      Swal.fire(alertConfig);
    }
  };

  const handleCloseTrainModal = () => {
    if (!training) {
      setShowTrainModal(false);
      setTrainProgress(0);
    }
  };

  // Funções de seleção de produtos
  const handleSelectProduct = (productId) => {
    setSelectedProducts((prev) => {
      const isSelected = prev.includes(productId);
      if (isSelected) {
        const newSelection = prev.filter((id) => id !== productId);
        setSelectAll(false);
        return newSelection;
      } else {
        // Verificar limite
        if (prev.length >= maxSelectionLimit) {
          Swal.fire({
            icon: "warning",
            title: "Limite atingido",
            text: `Você pode selecionar no máximo ${maxSelectionLimit} produtos por vez.`,
            confirmButtonColor: "#ffc107",
          });
          return prev;
        }

        const newSelection = [...prev, productId];

        // Verificar se todos os produtos da página atual estão selecionados
        const currentPageProducts = products.map((p) => p.id);
        const selectedOnCurrentPage = newSelection.filter((id) =>
          currentPageProducts.includes(id)
        );
        setSelectAll(
          selectedOnCurrentPage.length === currentPageProducts.length
        );

        return newSelection;
      }
    });
  };

  const handleSelectAllProducts = () => {
    if (selectAll) {
      // Desselecionar todos os produtos da página atual
      const currentPageProducts = products.map((p) => p.id);
      setSelectedProducts((prev) =>
        prev.filter((id) => !currentPageProducts.includes(id))
      );
      setSelectAll(false);
    } else {
      // Selecionar todos os produtos da página atual (respeitando o limite)
      const currentPageProducts = products.map((p) => p.id);
      setSelectedProducts((prev) => {
        const newProductsToAdd = currentPageProducts.filter(
          (id) => !prev.includes(id)
        );
        const totalAfterAdd = prev.length + newProductsToAdd.length;

        if (totalAfterAdd > maxSelectionLimit) {
          const canAdd = maxSelectionLimit - prev.length;
          if (canAdd <= 0) {
            Swal.fire({
              icon: "warning",
              title: "Limite atingido",
              text: `Você já selecionou o máximo de ${maxSelectionLimit} produtos.`,
              confirmButtonColor: "#ffc107",
            });
            return prev;
          }

          Swal.fire({
            icon: "warning",
            title: "Limite quase atingido",
            text: `Só é possível adicionar mais ${canAdd} produto(s). Limite máximo: ${maxSelectionLimit}`,
            confirmButtonColor: "#ffc107",
          });

          const limitedSelection = [
            ...prev,
            ...newProductsToAdd.slice(0, canAdd),
          ];
          setSelectAll(
            limitedSelection.filter((id) => currentPageProducts.includes(id))
              .length === currentPageProducts.length
          );
          return limitedSelection;
        }

        const newSelection = [...prev, ...newProductsToAdd];
        setSelectAll(true);
        return newSelection;
      });
    }
  };

  // Função para selecionar produtos de múltiplas páginas
  const handleSelectFromAllPages = async () => {
    try {
      setLoading(true);

      // Buscar todos os produtos (limitado a 100)
      const response = await ProductsServices.listProducts({
        page_size: maxSelectionLimit,
        search: searchTerm || undefined,
        agent_id: agentFilter || undefined,
        status: statusFilter || undefined,
        ordering: orderingFilter,
      });

      if (response.success) {
        const allProducts = response.data?.results || response.products || [];
        const allProductIds = allProducts.map((p) => p.id);

        setSelectedProducts(allProductIds);

        Swal.fire({
          icon: "success",
          title: "Produtos selecionados!",
          text: `${allProductIds.length} produtos foram selecionados de todas as páginas.`,
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      console.error("Erro ao selecionar todos os produtos:", error);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Não foi possível selecionar os produtos.",
        confirmButtonColor: "#dc3545",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClearSelection = () => {
    setSelectedProducts([]);
    setSelectAll(false);
  };

  const handleBulkDelete = async () => {
    if (selectedProducts.length === 0) return;

    const { value: isConfirmed } = await Swal.fire({
      title: "Confirmar Exclusão em Massa",
      html: `
        <div style="text-align: left; padding: 10px;">
          <p><strong>Produtos selecionados:</strong> ${selectedProducts.length}</p>
          <hr>
          <div style="color: #dc3545;">
            <p><strong>⚠️ ATENÇÃO:</strong></p>
            <p>Esta ação <strong>NÃO PODE SER REVERTIDA</strong>.</p>
            <p>Todos os produtos selecionados e suas imagens serão removidos permanentemente.</p>
          </div>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, excluir todos",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      width: 500,
    });

    if (!isConfirmed) return;

    try {
      const response = await ProductsServices.bulkDeleteProducts(
        selectedProducts
      );

      if (response.success) {
        const summary = response.data.summary;

        Swal.fire({
          icon: "success",
          title: "Exclusão concluída!",
          html: `
            <div style="text-align: left; padding: 10px;">
              <p><strong>✅ Produtos excluídos:</strong> ${
                summary.found_products || 0
              }</p>
              <p><strong>🖼️ Imagens excluídas:</strong> ${
                summary.total_images_deleted || 0
              }</p>
              ${
                summary.not_found_ids && summary.not_found_ids.length > 0
                  ? `<p><strong>⚠️ Produtos não encontrados:</strong> ${summary.not_found_ids.length}</p>`
                  : ""
              }
            </div>
          `,
          timer: 3000,
          showConfirmButton: false,
        });

        // Limpar seleção e recarregar dados
        setSelectedProducts([]);
        setSelectAll(false);
        loadProducts();
        loadProductStats();
      } else {
        throw new Error(response.error);
      }
    } catch (error) {
      console.error("Erro na exclusão em massa:", error);

      const alertConfig = ProductsServices.showErrorAlert(
        error,
        "Erro na exclusão em massa"
      );
      Swal.fire(alertConfig);
    }
  };

  const handleEditProduct = (productId) => {
    setEditingProductId(productId);
    setShowCreateModal(true);
  };

  const handleDeleteProduct = async (product) => {
    const { value: isConfirmed } = await Swal.fire({
      title: "Confirmar Exclusão",
      html: `
        <div style="text-align: left; padding: 10px;">
          <p><strong>Produto:</strong> ${product.name}</p>
          <p><strong>Agente:</strong> ${product.agentName}</p>
          <p><strong>Valor:</strong> ${product.formattedValue}</p>
          <hr>
          <p class="text-danger"><strong>⚠️ Atenção:</strong></p>
          <p>Esta ação não pode ser desfeita. O produto será removido permanentemente.</p>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, excluir",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      width: 500,
    });

    if (!isConfirmed) return;

    try {
      const response = await ProductsServices.deleteProduct(product.id);

      if (response.success) {
        Swal.fire({
          icon: "success",
          title: "Sucesso!",
          text: "Produto excluído com sucesso.",
          timer: 2000,
          showConfirmButton: false,
        });

        loadProducts();
        loadProductStats();
      } else {
        throw new Error(response.error);
      }
    } catch (error) {
      console.error("Erro ao excluir produto:", error);

      // Usar a nova função de tratamento de erros amigáveis
      const alertConfig = ProductsServices.showErrorAlert(
        error,
        "Erro ao excluir produto"
      );
      Swal.fire(alertConfig);
    }
  };

  const renderPagination = () => {
    const startItem = totalCount > 0 ? (currentPage - 1) * pageSize + 1 : 0;
    const endItem = Math.min(currentPage * pageSize, totalCount);

    const pageSizeOptions = [10, 25, 50, 100];

    // Componente do seletor de page size
    const renderPageSizeSelector = () => (
      <div className="d-flex align-items-center">
        <span className="text-muted me-2">Mostrar:</span>
        <Form.Select
          size="sm"
          value={pageSize}
          onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
          style={{ width: "80px" }}
          className="me-2"
        >
          {pageSizeOptions.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </Form.Select>
        <span className="text-muted">por página</span>
      </div>
    );

    // Se não há itens suficientes para paginação, mostrar só o seletor de page size
    if (totalPages <= 1) {
      return (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <div className="text-muted">
            {totalCount > 0
              ? `Mostrando ${startItem} a ${endItem} de ${totalCount} produtos`
              : "Nenhum produto encontrado"}
          </div>
          {renderPageSizeSelector()}
        </div>
      );
    }

    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Button
          key={i}
          variant={i === currentPage ? "primary" : "outline-primary"}
          size="sm"
          className="me-1"
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </Button>
      );
    }

    return (
      <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
        <div className="text-muted">
          Mostrando {startItem} a {endItem} de {totalCount} produtos
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          {renderPageSizeSelector()}
          <div className="d-flex justify-content-center align-items-center">
            <Button
              variant="outline-primary"
              size="sm"
              className="me-2"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(currentPage - 1)}
            >
              Anterior
            </Button>
            {pages}
            <Button
              variant="outline-primary"
              size="sm"
              className="ms-2"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              Próximo
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const renderHeader = () => {
    return (
      <div className="page-header pt-4 pt-md-0 mb-4">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-center w-100">
          <div>
            <h4 className="d-flex">
              <IconBox size={24} className="me-2" />
              Produtos
            </h4>
            {agent && (
              <p className="text-muted">
                Catálogo de produtos do agente <strong>{agent.name}</strong>
              </p>
            )}
          </div>
          <div>
            <Button
              variant="outline-primary"
              className="px-2 m-1"
              onClick={handleTrainAgent}
            >
              <IconBrain size={16} className="me-1" />
              Treinar Agente
            </Button>

            <Button
              variant="outline-primary"
              className="px-2 m-1"
              onClick={handleImportProducts}
            >
              <IconUpload size={16} className="me-1" />
              Importar
            </Button>

            <Button
              variant="primary"
              className="px-3 m-1"
              onClick={handleCreateProduct}
            >
              <IconPlus size={16} className="me-1" />
              Novo Produto
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="products-hub p-2 p-md-4">
      {renderHeader()}
      <div className="d-flex gap-2 align-items-center">
        {selectedProducts.length > 0 && (
          <>
            <div className="d-flex align-items-center me-3">
              <Badge bg="primary" className="me-2">
                {selectedProducts.length}/{maxSelectionLimit} selecionados
              </Badge>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={handleClearSelection}
                title="Limpar seleção"
              >
                <IconX size={14} />
              </Button>
            </div>
            <Button variant="danger" onClick={handleBulkDelete}>
              <IconTrash size={16} className="me-1" />
              Deletar Selecionados ({selectedProducts.length})
            </Button>
          </>
        )}
      </div>

      {/* Filtros e Busca */}
      <ProductSearch
        searchTerm={searchTerm}
        agentFilter={agentFilter}
        statusFilter={statusFilter}
        orderingFilter={orderingFilter}
        onSearch={handleSearch}
        onAgentFilter={() => {}}
        onStatusFilter={handleStatusFilter}
        onOrderingFilter={handleOrderingFilter}
        totalCount={totalCount}
        hideAgentFilter={true}
      />

      {/* Lista de Produtos */}
      <Card>
        <Card.Header className="d-flex gap-2 align-items-center">
          <IconBox size={20} />
          <h5 className="mb-0">Lista de Produtos</h5>

          {/* Controles de seleção avançada */}
          <div className="d-flex gap-2 ms-auto align-items-center">
            <Badge bg="secondary">
              {totalCount} produto{totalCount !== 1 ? "s" : ""}
            </Badge>
          </div>
        </Card.Header>
        <Card.Body>
          {/* Indicador de seleção entre páginas */}
          {selectedProducts.length > 0 && (
            <Alert
              variant="info"
              className="d-flex align-items-center justify-content-between mb-3"
            >
              <div className="d-flex align-items-center">
                <IconCheck size={16} className="me-2" />
                <span>
                  <strong>{selectedProducts.length}</strong> produto
                  {selectedProducts.length !== 1 ? "s" : ""} selecionado
                  {selectedProducts.length !== 1 ? "s" : ""}
                  {(() => {
                    const currentPageIds = products.map((p) => p.id);
                    const selectedOnCurrentPage = selectedProducts.filter(
                      (id) => currentPageIds.includes(id)
                    ).length;
                    const selectedOnOtherPages =
                      selectedProducts.length - selectedOnCurrentPage;

                    if (selectedOnOtherPages > 0) {
                      return (
                        <span className="text-muted">
                          {" "}
                          ({selectedOnCurrentPage} nesta página,{" "}
                          {selectedOnOtherPages} em outras páginas)
                        </span>
                      );
                    }
                    return null;
                  })()}
                </span>
              </div>
              <Button
                variant="outline-info"
                size="sm"
                onClick={handleClearSelection}
              >
                Limpar seleção
              </Button>
            </Alert>
          )}

          {loading ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ minHeight: "200px" }}
            >
              <div className="text-center">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Carregando produtos...</p>
              </div>
            </div>
          ) : products.length > 0 ? (
            <>
              <ProductTable
                products={products}
                onEdit={handleEditProduct}
                onDelete={handleDeleteProduct}
                selectedProducts={selectedProducts}
                selectAll={selectAll}
                onSelectProduct={handleSelectProduct}
                onSelectAllProducts={handleSelectAllProducts}
              />
              {renderPagination()}
            </>
          ) : (
            <Alert variant="info" className="text-center">
              <IconBox size={24} className="mb-2" />
              <p className="mb-0">
                {searchTerm || agentFilter || statusFilter
                  ? "Nenhum produto encontrado com os filtros aplicados."
                  : "Nenhum produto cadastrado."}
              </p>
              <small>
                {searchTerm || agentFilter || statusFilter
                  ? "Tente ajustar os filtros de busca."
                  : "Crie seu primeiro produto para começar."}
              </small>
            </Alert>
          )}
        </Card.Body>
      </Card>

      {/* Modal de Importação */}
      <Modal
        show={showImportModal}
        onHide={handleCloseImportModal}
        size="lg"
        centered
        backdrop={importing ? "static" : true}
        keyboard={!importing}
      >
        <Modal.Header closeButton={!importing}>
          <Modal.Title className="d-flex align-items-center">
            <IconUpload size={24} className="me-2" />
            Importar Produtos
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info" className="mb-4">
            <div className="d-flex align-items-start">
              <IconFileText size={20} className="me-2 mt-1" />
              <div>
                <strong>Instruções de Importação:</strong>
                <ul className="mb-2 mt-2">
                  <li>Baixe o arquivo exemplo para ver a estrutura correta</li>
                  <li>Arquivos aceitos: CSV e Excel (.xlsx)</li>
                  <li>Tamanho máximo: 5MB</li>
                  <li>Campos obrigatórios: name, value</li>
                  <li>
                    Campos opcionais: description, link, availability, order
                  </li>
                </ul>
                <Button
                  variant="outline-info"
                  size="sm"
                  onClick={handleDownloadSample}
                  disabled={importing}
                >
                  <IconDownload size={14} className="me-1" />
                  Baixar Arquivo Exemplo
                </Button>
              </div>
            </div>
          </Alert>

          <Form>
            <Row>
              <Col md={12} className="mb-3">
                <Form.Label>
                  Arquivo <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileSelect}
                  disabled={importing}
                />
                {selectedFile && (
                  <Form.Text className="text-success">
                    ✓ Arquivo selecionado: {selectedFile.name} (
                    {(selectedFile.size / 1024).toFixed(1)} KB)
                  </Form.Text>
                )}
              </Col>
            </Row>

            {importing && (
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <small className="text-muted">Importando produtos...</small>
                  <small className="text-muted">{importProgress}%</small>
                </div>
                <ProgressBar now={importProgress} animated />
              </div>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleCloseImportModal}
            disabled={importing}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleImportFile}
            disabled={importing || !selectedFile}
          >
            {importing ? (
              <>
                <Spinner size="sm" className="me-1" />
                Importando...
              </>
            ) : (
              <>
                <IconUpload size={16} className="me-1" />
                Importar Produtos
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de Treinamento do Agente */}
      <Modal
        show={showTrainModal}
        onHide={handleCloseTrainModal}
        size="lg"
        centered
        backdrop={training ? "static" : true}
        keyboard={!training}
      >
        <Modal.Header closeButton={!training}>
          <Modal.Title className="d-flex align-items-center">
            <IconBrain size={24} className="me-2" />
            Treinar Agente com Produtos
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info" className="mb-4">
            <div className="d-flex align-items-start">
              <IconBrain size={20} className="me-2 mt-1" />
              <div>
                <strong>Treinamento RAG com Produtos:</strong>
                <ul className="mb-2 mt-2">
                  <li>
                    O agente será treinado com todos os produtos cadastrados
                  </li>
                  <li>Serão criados embeddings dos dados dos produtos</li>
                  <li>O agente poderá responder perguntas sobre produtos</li>
                  <li>Embeddings anteriores deste agente serão removidos</li>
                </ul>
              </div>
            </div>
          </Alert>

          {agent && (
            <Alert variant="primary" className="mb-4">
              <div className="d-flex align-items-center">
                <IconBrain size={20} className="me-2" />
                <div>
                  Agente a ser treinado: {agent.agent_name}
                  <br />
                  <small className="text-muted">{agent.agent_job}</small>
                </div>
              </div>
            </Alert>
          )}

          {training && (
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <small className="text-muted">Treinando agente...</small>
                <small className="text-muted">{trainProgress}%</small>
              </div>
              <ProgressBar now={trainProgress} animated />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleCloseTrainModal}
            disabled={training}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleTrainAgentRAG}
            disabled={training}
          >
            {training ? (
              <>
                <Spinner size="sm" className="me-1" />
                Treinando...
              </>
            ) : (
              <>
                <IconBrain size={16} className="me-1" />
                Iniciar Treinamento
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de Criação/Edição do Produto */}
      <Modal
        show={showCreateModal}
        onHide={handleCancelCreate}
        size="lg"
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center">
            <IconBox size={24} className="me-2" />
            {editingProductId ? "Editar Produto" : "Novo Produto"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {showCreateModal && (
            <ProductFormWrapper
              productId={editingProductId}
              onSuccess={handleProductCreated}
              onCancel={handleCancelCreate}
            />
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ProductsList;
